import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  CardBody,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { format, subMonths } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fallbackDate } from "../../config/dashboardDefaults";

const specialOptions = [{ label: "Year-to-date", value: "year_to_date" }];

const getDateForOption = (value, baseDate = new Date()) => {
  switch (value) {
    default: {
      if (/^\d{4}-\d{2}$/.test(value)) {
        const parsed = new Date(`${value}-01T00:00:00`);
        const endDate = new Date(
          parsed.getFullYear(),
          parsed.getMonth() + 1,
          0,
        );
        const safeEndDate = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
        );
        return { endDate: safeEndDate, ytd: false };
      }
      return { endDate: null, ytd: false };
    }
  }
};

const CalendarPicker = ({
  initialDate,
  onSave,
  onClose,
  direction = "right",
}) => {
  const [endDate, setEndDate] = useState(initialDate);
  const ref = useRef();

  useOutsideClick({
    ref,
    handler: () => onClose?.(),
  });

  const handleDateSelection = (date) => {
    const selectedDate = new Date(date);
    const lastDayOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0,
    );
    setEndDate(lastDayOfMonth);
  };

  const handleSave = () => {
    onSave?.(endDate);
    onClose?.();
  };

  return (
    <Card
      ref={ref}
      textAlign="right"
      p={0}
      zIndex={9999}
      width="290px"
      position="absolute"
      mt={2}
      right={direction === "right" ? 0 : undefined}
      left={direction === "left" ? 0 : undefined}
      backgroundColor="white"
      boxShadow="md"
    >
      <CardBody p={1}>
        <Box>
          <DatePicker
            selected={endDate}
            onChange={handleDateSelection}
            endDate={endDate}
            inline
            showMonthYearPicker
            dateFormat="MM/yyyy"
          />
          <Button
            m="6px 9px 10px"
            height="32px"
            width="81px"
            backgroundColor="#536DEC"
            color="white"
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      </CardBody>
    </Card>
  );
};

const DashboardDropdown = ({
  onChange,
  dateFromNav,
  endDate,
  setEnd,
  category,
  setCategory,
  direction = "right",
  monthAmount = 12,
  alterButtonSize = false,
  includeAllFromMin = false,
}) => {
  const [selected, setSelected] = useState(category);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const buttonRef = useRef(null);

  const MIN_DATE = new Date(2024, 0, 1);

  const getMonthOptions = useCallback(
    (baseDate) => {
      const options = [];

      let referenceDate = new Date(baseDate);
      if (isNaN(referenceDate.getTime())) {
        referenceDate = new Date(fallbackDate);
      }

      let current = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        1,
      );

      let count = 0;

      while (current >= MIN_DATE) {
        options.push({
          label: format(current, "MMMM yyyy"),
          value: format(current, "yyyy-MM"),
        });

        current = subMonths(current, 1);
        count++;

        if (!includeAllFromMin && count >= monthAmount) break;
      }

      return options;
    },
    [monthAmount, includeAllFromMin, MIN_DATE],
  );

  const [monthOptions, setMonthOptions] = useState(
    getMonthOptions(
      new Date(
        new Date(dateFromNav).getFullYear(),
        new Date(dateFromNav).getMonth() + 1,
        0,
      ),
    ),
  );

  useEffect(() => {
    if (category) setSelected(category);
  }, [category]);

  useEffect(() => {
    setLocalEndDate(endDate);
  }, [endDate]);

  useEffect(() => {
    setMonthOptions(
      getMonthOptions(
        new Date(
          new Date(dateFromNav).getFullYear(),
          new Date(dateFromNav).getMonth() + 1,
          0,
        ),
      ),
    );
  }, [dateFromNav, getMonthOptions]);

  const findLabel = (val) =>
    [...specialOptions, ...monthOptions].find((o) => o.value === val)?.label;

  const handleSelect = (value) => {
    if (value === "custom") {
      setSelected(value);
      setCategory?.(value);
      setShowCustomPicker(true);
      return;
    }

    setSelected(value);
    setCategory?.(value);

    const { endDate: derivedEndDate } = getDateForOption(value, dateFromNav);
    setEnd?.(derivedEndDate);

    localStorage.setItem(
      "dashboardDate",
      derivedEndDate.toISOString().split("T")[0],
    );
    window.dispatchEvent(new Event("dashboardDate-changed"));
    const label = findLabel(value);
    onChange?.({ label, value, endDate: derivedEndDate });
  };

  const getStyles = (isSelected) => ({
    bg: isSelected ? "#536DEC" : "white",
    color: isSelected ? "white" : "black",
    fontWeight: "normal",
    borderRadius: "8px",
    _hover: {
      color: isSelected ? "white" : "#536DEC",
      bg: isSelected ? "#536DEC" : "white",
    },
    _focus: { bg: isSelected ? "#536DEC" : "white" },
  });

  return (
    <Box position="relative" display="inline-block">
      <Menu
        isLazy
        placement={direction === "left" ? "bottom-start" : "bottom-end"}
        portal={false}
      >
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          width="auto"
          bg="white"
          color="black"
          border={"1px solid #D1D4DA"}
          _hover={{ bg: "white", border: "1px solid #536DEC" }}
          _expanded={{ bg: "white", border: "1px solid #536DEC" }}
          ref={buttonRef}
        >
          {localEndDate ? format(localEndDate, "MMM yyyy") : "Select date"}
        </MenuButton>

        <MenuList
          border={"1px solid #D1D4DA"}
          bg="white"
          height={"auto"}
          maxH="min(476px, 60vh)"
          width="210px"
          maxW="170px"
          left="50%"
          overflowY="auto"
          transform="translateX(-50%)"
          p={2}
          sx={{
            pr: "5px",
            pl: "5px",
            boxSizing: "content-box",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#30384A",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "gray.100",
              borderRadius: "4px",
              marginBlock: "4px",
            },
          }}
        >
          <VStack
            align="start"
            width={alterButtonSize === true ? "200px" : "100%"}
            spacing={1}
          >
            {monthOptions.map((opt) => {
              const isSelected = selected === opt.value;
              return (
                <MenuItem
                  key={opt.value}
                  {...getStyles(isSelected)}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </MenuItem>
              );
            })}
          </VStack>
        </MenuList>
      </Menu>

      {showCustomPicker && (
        <CalendarPicker
          direction={direction}
          initialDate={endDate}
          onSave={(date) => {
            setEnd?.(date);
            setCategory?.("custom");
            onChange?.({
              label: "Custom",
              value: "custom",
              endDate: date,
            });
          }}
          onClose={() => setShowCustomPicker(false)}
        />
      )}
    </Box>
  );
};

export default DashboardDropdown;
